import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store/store";
import { dequal } from "dequal";
import { syncFromURL } from "../../store/slices/userSelection/userSelection";
import { updateScreenLevelSelection } from "@/store/slices/screenLevelSelection/screenLevelSelection";
import { parseURLParams } from "@/utils/url";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import {
  AGE_START,
  BRANDS,
  BUTTERLFY,
  DIFFERENTIATION,
  FILTERS,
  MARKETS,
  MODE,
  PERCEPTUAL,
  PERIOD_END_DATE,
  PERIOD_START_DATE,
  POINT_IN_TIME,
  POST_END_DATE,
  POST_START_DATE,
  PROFILE,
  PROFILE_GROUP,
  PROFILE_RESPONSE,
  PROFILE_METRIC,
  QUESTION_OPTIONS,
  SECONDARY_BRANDS,
  SEGMENTS,
  TRENDS_METRIC,
  TRENDS_RESPONSE,
  VIEW_ID,
  URL_BY_BRAND,
  URL_BY_MARKET,
  URL_BY_SEGMENT,
  PRE_POST,
} from "@/constant/constantsThrougoutProject";
import { ALL_URL_KEYS } from "@/constant/url";
import {
  getUpdateUrlParameters,
  STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION,
} from "./utils";
import { OLD_URLS } from "./oldUtils";
import { getNewURL } from "./oldUtils";
import { getFeatureRedirect } from "@/utils/featureStatus";
import { AGE_SEGMENT_LABELS } from "@/constant/ageSegment";
import ShowToaster from "@/toaster";

export const useURLSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isInitialDataUpdated, setIsInitialDataUpdated] =
    useState<boolean>(false);

  const activeGroupDataIds = useSelector(
    (state: RootState) => {
      return {
        brands: state.activeGroup.brands.ids,
        markets: [...state.activeGroup.markets.markets.ids].sort((a, b) => {
          const marketA = state.activeGroup.markets.markets.entities[a];
          const marketB = state.activeGroup.markets.markets.entities[b];

          if (!marketA || !marketB) return 0;

          const aIsUS = marketA.identity.toLowerCase().startsWith("us");
          const bIsUS = marketB.identity.toLowerCase().startsWith("us");

          if (aIsUS && !bIsUS) return -1;
          if (!aIsUS && bIsUS) return 1;

          if (aIsUS && bIsUS) {
            return marketA.identity.length - marketB.identity.length;
          }

          return 0;
        }),
        brandsEntities: state.activeGroup.brands.entities,
        segments: state.activeGroup.segments.segments.ids,
        ageSegmentId: state.activeGroup.segments.segments.ids?.find(
          (segmentId: number) => {
            if (
              AGE_SEGMENT_LABELS?.includes(
                state.activeGroup.segments.segments.entities[segmentId]?.name,
              ) &&
              state.activeGroup.segments.segments.entities[segmentId]
                ?.categoryType === "DEFAULT"
            ) {
              return true;
            }
            return false;
          },
        ),
        activeSegments: state.activeGroup.segments.segments.ids?.filter(
          (segmentId: number) => {
            if (
              state.activeGroup.segments.segments.entities[segmentId]?.isActive
            ) {
              return true;
            }
            return false;
          },
        ),
        filters: state.activeGroup.filters.filters.ids?.reduce(
          (acc: number[], curr: number) => {
            const currentFilter =
              state.activeGroup.filters.filters?.entities[curr];
            const filterOptionsIds = currentFilter?.filterOptions?.map(
              (item) => item.filterOptionId,
            );
            return [...acc, ...filterOptionsIds];
          },
          [],
        ),
      };
    },
    (prev, next) => dequal(prev, next),
  );

  const markets = useSelector(
    (state: RootState) => state.activeGroup.markets.markets,
    (prev, next) => dequal(prev, next),
  );

  const userSelection = useSelector(
    (state: RootState) => state.userSelection,
    (prev, next) => dequal(prev, next),
  );

  const featureStatus = useSelector(
    (state: RootState) => state.featureStatus,
    (prev, next) => dequal(prev, next),
  );

  const { questions: allQuestions, isQuestionsFetchedInitially } = useSelector(
    (state: RootState) => state.activeGroup.questions,
    dequal,
  );

  useEffect(() => {
    if (
      activeGroupDataIds?.brands?.length > 0 &&
      activeGroupDataIds?.markets?.length > 0
    ) {
      if (OLD_URLS?.includes(location.pathname?.toLowerCase())) {
        const { pathname, url } = getNewURL(location.search, markets);
        let updatedParams = parseURLParams(new URLSearchParams(url));
        const urlUpdated = getUpdateUrlParameters(
          pathname,
          updatedParams,
          userSelection,
          activeGroupDataIds,
        );
        setIsInitialDataUpdated(true);
        navigate({
          pathname: pathname,
          search: urlUpdated,
        });
      } else {
        let updatedParams = parseURLParams(
          new URLSearchParams(location.search),
        );
        const redirectUrl = getFeatureRedirect(featureStatus as any);
        const url = getUpdateUrlParameters(
          location.pathname === "/" ? redirectUrl : location.pathname,
          updatedParams,
          userSelection,
          activeGroupDataIds,
        );
        setIsInitialDataUpdated(true);
        navigate({
          pathname: location.pathname === "/" ? redirectUrl : location.pathname,
          search: url,
        });
      }
    }
  }, [activeGroupDataIds]);

  useEffect(() => {
    if (isInitialDataUpdated) {
      const searchParams = new URLSearchParams(location.search);
      const updatedParams = parseURLParams(searchParams);
      const urlTokens = location.pathname.split("/");
      const module = urlTokens?.[1] || "visibility";
      const isReports = urlTokens?.length > 2 && urlTokens[1] === "reports";
      const reportId = isReports ? urlTokens[2] : null;

      if (Object.keys(updatedParams).length > 0) {
        const currentPathConfig = ROUTES_CHILDREN_LOOKUP[location.pathname];
        const finalParams: Record<string, any> = {};

        // 1. Determine mode
        let mode: string = typeof updatedParams?.mode === "string" ? updatedParams.mode : "POINT_IN_TIME";
        const vizType = updatedParams?.visualizationType;
        if (
          mode === POINT_IN_TIME &&
          (vizType === PERCEPTUAL || vizType === BUTTERLFY || vizType === DIFFERENTIATION || vizType === PROFILE)
        ) {
          mode = vizType;
        }

        let ageRange: number[] = [];
        const modeKey = currentPathConfig?.modeOptions?.includes(mode) ? mode : "POINT_IN_TIME";
        const urlKeysToProcess = currentPathConfig?.urlKeysMapping?.[modeKey] || [];

        // 2. Process each URL key mapping efficiently
        urlKeysToProcess.forEach((key: string) => {
          let value: any = updatedParams[key];

          if ([FILTERS, SEGMENTS, QUESTION_OPTIONS].includes(key) && !value) {
            value = [];
          }

          if (value !== undefined && value !== null && value !== "") {
            switch (key) {
              case BRANDS:
              case MARKETS:
                if (currentPathConfig?.multipleOptionsForKeys[modeKey]?.includes(key)) {
                  finalParams[key] = activeGroupDataIds?.[key]?.filter((id: number) => value.includes(id)) || [];
                } else {
                  finalParams[key] = value?.length > 0 ? [value[0]] : [];
                }
                break;
              
              case SECONDARY_BRANDS:
              case PERIOD_START_DATE:
              case PERIOD_END_DATE:
              case POST_START_DATE:
              case POST_END_DATE:
              case TRENDS_METRIC:
              case TRENDS_RESPONSE:
              case VIEW_ID:
                finalParams[key] = value;
                break;

              case SEGMENTS:
                finalParams[key] = value || [];
                break;

              case FILTERS:
                finalParams[key] = Array.isArray(value) ? value.filter((item: any) => typeof item === "number" && !Number.isNaN(item)) : [];
                break;

              case AGE_START:
                ageRange = [Number(updatedParams?.ageStart), Number(updatedParams?.ageEnd)].filter(item => item > 0);
                break;
                
              case MODE:
                finalParams[key] = currentPathConfig?.modeOptions?.includes(value) ? value : POINT_IN_TIME;
                break;

              case QUESTION_OPTIONS:
                finalParams[key] = value.reduce((acc: Record<string, number[]>, val: string) => {
                  const [qId, optId] = val.split("-");
                  if (qId && optId) {
                    if (!acc[qId]) acc[qId] = [];
                    acc[qId].push(Number(optId));
                  }
                  return acc;
                }, {});
                break;

              case PROFILE_GROUP:
              case PROFILE_METRIC:
              case PROFILE_RESPONSE:
                finalParams[key] = !isNaN(value) ? Number(value) : value;
                break;

              default:
                if (ALL_URL_KEYS?.includes(key)) {
                  finalParams[key] = value;
                }
            }
          }
        });

        // 3. Determine screen type for screen level selection
        const pathIdStr = String(currentPathConfig?.id);
        const screenType = pathIdStr.includes(URL_BY_BRAND) ? URL_BY_BRAND :
                           pathIdStr.includes(URL_BY_SEGMENT) ? URL_BY_SEGMENT :
                           pathIdStr.includes(URL_BY_MARKET) ? URL_BY_MARKET : URL_BY_BRAND;

        let screenTypeForScreenLevelSelection = screenType;
        const vizString = updatedParams.visualizationType as string;
        const modeString = updatedParams.mode as string;
        const fallbackMode = modeString || "POINT_IN_TIME";

        if (STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION.includes(vizString)) {
          screenTypeForScreenLevelSelection = `${screenType}|${fallbackMode}|${vizString}`;
        }
        // use brand config instead of PRE_POST for eq's brand prepost mode
         else if ( !(currentPathConfig?.id === "exclusivequestions/brand" && fallbackMode === PRE_POST) && STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION.includes(modeString)) {
          screenTypeForScreenLevelSelection = `${fallbackMode}`;
        }

        // 4. Dispatch screen level selection
        dispatch(updateScreenLevelSelection({
          screenType: screenTypeForScreenLevelSelection,
          value: {
            brands: finalParams.brands,
            markets: finalParams.markets,
            segments: finalParams.segments,
            secondaryBrands: finalParams.secondaryBrands || [],
          },
        }));

        // 5. Reputation specific logic
        if (location.pathname.startsWith("/reputation")) {
          const marketIds = finalParams.market ? [finalParams.market] : finalParams.markets || [];
          const brandIds = finalParams.brand ? [finalParams.brand] : finalParams.brands || [];

          if (marketIds.length > 0 && brandIds.length > 0) {
            const reputationValidBrandIds = new Set<number>();
            Object.values(activeGroupDataIds.brandsEntities).forEach((brand: any) => {
              if (brand && Array.isArray(brand.reputationMarketIds) && marketIds.some((mid: number) => brand.reputationMarketIds.includes(mid))) {
                reputationValidBrandIds.add(brand.id);
              }
            });

            let validBrandIds = brandIds.filter((id: number) => reputationValidBrandIds.has(id));
            if (validBrandIds.length === 0 && reputationValidBrandIds.size > 0) {
              validBrandIds = [Array.from(reputationValidBrandIds)[0]];
            }
            finalParams.brands = validBrandIds;
          }
        }

        // 6. Dispatch final syncFromURL
        dispatch(syncFromURL({
          ...finalParams,
          module,
          reportId,
          screenType,
          ageRange: ageRange.length > 1 ? ageRange : null,
        }));
      } else {
        // Fallback to userSelection
        dispatch(syncFromURL({
          ...userSelection,
          module,
          reportId,
        }));
      }
    }
  }, [navigate, location]);

  useEffect(() => {
    if (isQuestionsFetchedInitially) {
      const params = new URLSearchParams(location.search);
      const questionOptionsFromURL = params.getAll(QUESTION_OPTIONS);

      if (questionOptionsFromURL.length > 0) {
        const validQuestionOptions = questionOptionsFromURL.filter((option) => {
          const [questionKey] = option.split("-");
          return allQuestions.entities[questionKey];
        });

        const inValidQuestionKeys: string[] = [
          ...new Set(
            questionOptionsFromURL
              .map((option) => option.split("-")[0])
              .filter((questionKey) => !allQuestions.entities[questionKey]),
          ),
        ];

        if (inValidQuestionKeys?.length > 0) {
          const message: string = `${inValidQuestionKeys.length > 1 ? "questions are" : "question is"} not applicable for the selected market.`;
          // ShowToaster.info(inValidQuestionKeys?.join(',') + ' ' + message)
        }

        if (validQuestionOptions.length < questionOptionsFromURL.length) {
          const newParams = new URLSearchParams(location.search);
          newParams.delete(QUESTION_OPTIONS);
          validQuestionOptions.forEach((option) => {
            newParams.append(QUESTION_OPTIONS, option);
          });
          navigate({ search: newParams.toString() }, { replace: true });
        }
      }
    }
  }, [isQuestionsFetchedInitially, allQuestions.entities, location, navigate]);
};

export default useURLSync;
